import {
  faArrowsSpin,
  faPlus,
  faRefresh
} from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Button, Grid } from '@mui/material';
import { useAtom } from 'jotai';
import { useMemo } from 'react';

import CheckPermissions from '@/components/CheckPermissions';
import ExportButton from '@/components/ExportButton';
import PERMISSIONS from '@/constants/auth.constants';
import {
  useDeviceDialog,
  useFetchDeviceType
} from '@/features/DeviceCreate/DeviceCreate.hooks';
import { deviceTypesAtom } from '@/features/DeviceCreate/DeviceCreate.store';
import SearchBar, { getSearchableSelectOptions } from '@/features/SearchBar';
import useFetchEnvironment, {
  useCrudGuard
} from '@/pages/Administration/Administration.hooks';
import {
  customEventAtom,
  devicesAtom,
  useStyles
} from '@/pages/Administration/Devices';
import DEVICE_QUICK_FILTERS from '@/pages/Administration/Devices/Devices.constants';
import {
  getDeviceTypeFilterOptions,
  getLogSourceFilterOptions,
  getStatusFilterOptions
} from '@/pages/Administration/Devices/Devices.service';
import {
  CustomEventTypes,
  devicesExportAtom,
  devicesFiltersAtom,
  filteredDevicesAtom,
  syncInProgressAtom
} from '@/pages/Administration/Devices/Devices.store';
import { useSyncDevicesDialog } from '@/pages/Administration/Devices/SyncDevices/SyncDevices.hooks';
import { selectedOrganizationAtom } from '@/pages/Organizations';

import type { DeviceTableHeaderProps } from '../DevicesTable.types';
import { searchbarInputContainerStyle } from './DevicesTableHeader.styles';

/**
 * Header component for DevicesTable
 *
 * Provides:
 * - Search bar with quick filters (device type, status, log sources)
 * - Add Device button
 * - Sync Devices button
 * - Export button (CSV/JSON)
 * - Refresh button
 *
 * @param props - Component props
 * @returns JSX element with table header controls
 *
 * @example
 * ```tsx
 * <DevicesTableHeader refreshDevices={handleRefresh} />
 * ```
 */
const DevicesTableHeader = (props: DeviceTableHeaderProps) => {
  const { collapsed = false, refreshDevices: refreshDevicesProp } = props;
  const { classes } = useStyles({
    deviceDetailsCollapsed: collapsed
  });

  const { openDeviceDialog } = useDeviceDialog();
  const { openSyncDevicesDialog } = useSyncDevicesDialog();

  const [devicesFilters, setDevicesFilters] = useAtom(devicesFiltersAtom);
  const [deviceTypes] = useAtom(deviceTypesAtom);
  const [devices] = useAtom(devicesAtom);
  const [exportData] = useAtom(devicesExportAtom);
  const [, setCustomEvent] = useAtom(customEventAtom);
  const [filteredDevices] = useAtom(filteredDevicesAtom);
  const [selectedOrganization] = useAtom(selectedOrganizationAtom);
  const [syncInProgress] = useAtom(syncInProgressAtom);

  useFetchEnvironment();
  useFetchDeviceType();

  /**
   * Opens the device creation dialog
   */
  const createDeviceHandler = useCrudGuard(() => {
    openDeviceDialog('New');
  });

  /**
   * Opens the device sync dialog
   */
  const syncDeviceHandler = useCrudGuard(() => {
    openSyncDevicesDialog('newSyncDevices');
  });

  /**
   * Refreshes device listing data
   * Uses custom event if no prop handler provided
   */
  const refreshDevices = () => {
    if (refreshDevicesProp) {
      refreshDevicesProp();
    } else {
      setCustomEvent(CustomEventTypes.REFRESH_DEVICE_LISTING);
    }
  };

  /**
   * Builds quick filter options dynamically based on current device data
   */
  const devicesQuickFilters = useMemo(() => {
    const qFilters = DEVICE_QUICK_FILTERS.map((filter) => {
      let filterOptions = [];

      if (filter.field === 'device_type_id') {
        filterOptions = getDeviceTypeFilterOptions(deviceTypes, devices);
      } else if (filter.field === 'status') {
        filterOptions = getStatusFilterOptions(devices);
      } else if (filter.field === 'sources') {
        filterOptions = getLogSourceFilterOptions(devices);
      } else {
        filterOptions = getSearchableSelectOptions(
          devices,
          filter.field,
          filter.title
        );
      }

      return {
        ...filter,
        filterOptions: [...filter.filterOptions, ...filterOptions]
      };
    });
    return qFilters;
  }, [deviceTypes, selectedOrganization, filteredDevices]);

  return (
    <Grid container spacing={2} sx={{ justifyContent: 'space-between' }}>
      <Grid>
        <SearchBar
          className="px-8 pb-4"
          searchbarInputContainerStyle={searchbarInputContainerStyle}
          searchableSelectFilters={devicesQuickFilters}
          selectedFilters={devicesFilters}
          onFilterChange={setDevicesFilters}
        />
      </Grid>
      <Grid
        className={classes.searchbarActionBtnGroup}
        sx={{
          maxHeight: '1.8rem'
        }}
        container
        spacing={2}
      >
        <CheckPermissions permissions={PERMISSIONS.DEVICES_CREATE}>
          <Button
            className={classes.floatInlineEnd}
            variant="contained"
            color="primary"
            size="medium"
            onClick={createDeviceHandler}
            startIcon={<FontAwesomeIcon icon={faPlus} />}
          >
            {collapsed && (
              <span style={{ marginLeft: '.5rem' }}>Add Device</span>
            )}
          </Button>
          <Button
            className={classes.floatInlineEnd}
            variant="contained"
            color="primary"
            size="medium"
            onClick={syncDeviceHandler}
            disabled={syncInProgress}
            startIcon={<FontAwesomeIcon icon={faArrowsSpin} />}
          >
            {collapsed && <span style={{ marginLeft: '.5rem' }}>Sync</span>}
          </Button>
        </CheckPermissions>

        <ExportButton
          data={exportData}
          fileName="pondurance-devices"
          json
          csv
        />
        <Button variant="outlined" onClick={() => refreshDevices()}>
          <FontAwesomeIcon size="lg" icon={faRefresh} />
        </Button>
      </Grid>
    </Grid>
  );
};

export default DevicesTableHeader;
